import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import type {
  NoticeCatalog,
  NoticeComposition,
  NoticeContextEvidence,
  NoticeContextField,
  NoticeRun,
  NoticeTerminologyNode,
} from './notice-fidelity.models';
import {
  applyNoticeReferenceCommand,
  noticeReferenceChoices,
  noticeReferenceContextMatches,
  noticeReferenceField,
  noticeSelectedReference,
  type NoticeReferenceCommand,
  type NoticeReferenceSelection,
} from './notice-reference-commands';

/** Contextual intentions only; the Page owner applies them to its current source. */
@Component({
  selector: 'phms-notice-reference-properties',
  standalone: true,
  imports: [CommonModule, FormsModule, UiButtonDirective, UiControlDirective],
  templateUrl: './notice-reference-properties.component.html',
  styleUrl: './notice-reference-properties.component.scss',
})
export class NoticeReferencePropertiesComponent implements OnChanges {
  @Input({ required: true }) composition!: NoticeComposition;
  @Input({ required: true }) catalog!: NoticeCatalog;
  @Input() selection: NoticeReferenceSelection | null = null;
  @Input() disabled = false;
  /** Exact per-reference renderer evidence; never a client-resolved sample. */
  @Input() resolvedText: string | null = null;
  @Input() proofLabel = '';
  @Input() termEvidence: NoticeContextEvidence['termEvidence'][number] | null =
    null;
  @Input() terminologyNode: NoticeTerminologyNode | null = null;
  @Output() readonly command = new EventEmitter<NoticeReferenceCommand>();
  @Output() readonly breakHistoryGroup = new EventEmitter<void>();
  @Output() readonly returnToText = new EventEmitter<void>();
  readonly unavailableEmptyFormat = Object.freeze({ unavailable: true });
  validationError = '';

  get current(): NoticeRun | undefined {
    return noticeSelectedReference(this.composition, this.selection);
  }

  get choices(): readonly { code: string; label: string }[] {
    return this.selection
      ? noticeReferenceChoices(
          this.composition,
          this.catalog,
          this.selection.kind,
        )
      : [];
  }

  get knownBinding(): boolean {
    return (
      !!this.current &&
      this.choices.some((choice) => choice.code === this.current!.value)
    );
  }

  get field(): NoticeContextField | undefined {
    const run = this.current;
    return run?.kind === 'field'
      ? noticeReferenceField(this.composition, this.catalog, run.value)
      : undefined;
  }

  get term():
    | NonNullable<NoticeCatalog['documentContext']>['terms'][number]
    | undefined {
    const run = this.current;
    return run?.kind === 'term' &&
      this.knownBinding &&
      noticeReferenceContextMatches(this.composition, this.catalog)
      ? this.catalog.documentContext!.terms.find(
          (term) => term.code === run.value,
        )
      : undefined;
  }

  get formats(): readonly string[] {
    return this.field?.formats ?? [];
  }

  get knownFormat(): boolean {
    const run = this.current;
    return (
      !!run && (run.format === undefined || this.formats.includes(run.format))
    );
  }

  get formatValue(): string | null | Readonly<{ unavailable: boolean }> {
    return this.current?.format === ''
      ? this.unavailableEmptyFormat
      : (this.current?.format ?? null);
  }

  get exactWordingAvailable(): boolean {
    return (
      !!this.current && this.resolvedText !== null && !!this.proofLabel.trim()
    );
  }

  get capturedTerm(): NoticeContextEvidence['termEvidence'][number] | null {
    const declared = this.term;
    const evidence = this.termEvidence;
    return this.exactWordingAvailable &&
      declared &&
      evidence &&
      evidence.code === this.current?.value &&
      evidence.tokenCode === declared.tokenCode &&
      evidence.configurationCode === declared.configurationCode &&
      evidence.text === this.resolvedText
      ? evidence
      : null;
  }
  get capturedNode(): NoticeTerminologyNode | null {
    const node = this.terminologyNode;
    return this.exactWordingAvailable &&
      node &&
      this.term &&
      node.tokenCode === this.term.tokenCode &&
      node.configurationCode === this.term.configurationCode &&
      node.text === this.resolvedText
      ? node
      : null;
  }

  missingBehaviorLabel(behavior: string): string {
    return behavior === 'withhold_referenced_value'
      ? 'A missing referenced value withholds the complete output.'
      : behavior;
  }

  changeBinding(code: string): void {
    this.emit({ type: 'binding', code });
  }

  changeFormat(format: string | null): void {
    this.emit({ type: 'format', format });
  }

  returnFocus(): void {
    if (!this.disabled && this.current) this.returnToText.emit();
  }

  ngOnChanges(): void {
    this.validationError = '';
  }

  private emit(command: NoticeReferenceCommand): void {
    if (this.disabled || !this.selection) return;
    const result = applyNoticeReferenceCommand(
      this.composition,
      this.catalog,
      this.selection,
      command,
    );
    if (!result.ok) {
      this.validationError = `This change was not applied. ${result.message}`;
      return;
    }
    this.validationError = '';
    if (JSON.stringify(result.composition) === JSON.stringify(this.composition))
      return;
    this.breakHistoryGroup.emit();
    try {
      this.command.emit(command);
    } finally {
      this.breakHistoryGroup.emit();
    }
  }
}
